import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { isJsonString } from "../../components/newHomePage/utils/newHomePageUtils";
import { getLocalStorage } from "../../utils/localStorageUtils";
import { setRecipeAndIngredients } from "../recipePage/recipePage.slice";
import { SearchFilteredRecipesInterface } from "../search/search.types";
import { RootState } from "../store/store";
import { HomeConfigAndCategoryToRecipeIdPayload, HomePageDataPayload, HomepageState, IngredientsQueryInterface, IngredientsTableInterface } from "./homepage.types";
import unionBy from "lodash/unionBy";
import { setGroceryListItemsAndIngredientsList } from "../groceryList/groceryList.slice";
const recipeList = getLocalStorage('recipeList')
const favoriteList = getLocalStorage('favoriteRecipes')

const homeVersion = parseInt(localStorage.getItem('mobile_version') || "0", 10) || 0;
const homeConfig = localStorage.getItem('mobile_config')
const filteredCategoryToRecipeData = localStorage.getItem('filteredCategoryToRecipeData');

const initialState: HomepageState = {
    recipeList: recipeList ? JSON.parse(recipeList) : [],
    experimentalRecipeList: [],
    filteredRecipes: [],
    filteredRecipesHome: [],
    userFavoriteRecipeIds: favoriteList ? JSON.parse(favoriteList) : [],
    recipeMetaData: [],
    ingredients: [],
    recipeCreationOption: null,
    homeVersion: homeVersion ?? null,
    homeConfig: homeConfig && isJsonString(homeConfig) ? JSON.parse(homeConfig) : null,
    categoryToRecipeId: filteredCategoryToRecipeData && isJsonString(filteredCategoryToRecipeData) ? JSON.parse(filteredCategoryToRecipeData) : {},
    refetchJson: false,
};

export const homepageSlice = createSlice({
    name: "homepage",
    initialState,
    reducers: {
        selectRecipeCreationOption: (state, action: PayloadAction<string | null>) => {
            state.recipeCreationOption = action.payload;
        },
        addUserFavorites: (state, action: PayloadAction<string>) => {
            state.userFavoriteRecipeIds.unshift(action.payload)
            localStorage.setItem('favoriteRecipes', JSON.stringify(state.userFavoriteRecipeIds));
        },

        removeUserFavorites: (state, action: PayloadAction<string>) => {
            const index = state.userFavoriteRecipeIds.indexOf(action.payload)
            state.userFavoriteRecipeIds.splice(index, 1)
            localStorage.setItem('favoriteRecipes', JSON.stringify(state.userFavoriteRecipeIds));
        },
        setUserFavorites: (state, action: PayloadAction<Array<string>>) => {
            state.userFavoriteRecipeIds = action.payload;
            localStorage.setItem('favoriteRecipes', JSON.stringify(state.userFavoriteRecipeIds));
        },
        setIngredientsList: (state, action: PayloadAction<Array<IngredientsQueryInterface>>) => {
            state.ingredients = unionBy([...state.ingredients, ...action.payload], 'id');
        },
        setAllRecipesData: (state, action: PayloadAction<{
            cuisinesList: Array<string>,
            ingredientsData: Array<IngredientsTableInterface>
            ingredientsList: Array<string>,
            mealTypeList: Array<string>,
            recipeMetaData: Array<SearchFilteredRecipesInterface>
        }>) => {
            state.ingredients = unionBy([...state.ingredients, ...action.payload.ingredientsData], 'id');
            state.recipeMetaData = action.payload.recipeMetaData;
        },
        setRecipeList: (state, action: PayloadAction<Array<SearchFilteredRecipesInterface>>) => {
            state.recipeList = action.payload;
        },
        setHomeConfigAndCategoryToRecipeId: (state, action: PayloadAction<HomeConfigAndCategoryToRecipeIdPayload>) => {
            state.homeConfig = action.payload.mobile_config;
            state.homeVersion = action.payload.mobile_version;
            state.categoryToRecipeId = action.payload.categoryToRecipeId;
        },
        setHomePageData: (state, action: PayloadAction<HomePageDataPayload>) => {
            state.homeVersion = action.payload.mobile_version;
            state.homeConfig = action.payload.mobile_config;
            state.recipeList = action.payload.recipes;
            state.ingredients = unionBy([...state.ingredients, ...action.payload.ingredients], 'id');
            state.categoryToRecipeId = action.payload.categoryToRecipeId;
            state.refetchJson = true;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(setRecipeAndIngredients, (state, action) => {
            state.ingredients = unionBy([...state.ingredients, ...action.payload.ingredients], 'id');
        }),
        builder.addCase(setGroceryListItemsAndIngredientsList, (state, action) => {
            state.ingredients = unionBy([...state.ingredients, ...action.payload.ingredientsList], 'id');
        })
    }
});

// Action creators are generated for each case reducer function
export const {
    setUserFavorites,
    removeUserFavorites,
    addUserFavorites,
    setIngredientsList,
    setAllRecipesData,
    selectRecipeCreationOption,
    setRecipeList,
    setHomePageData,
    setHomeConfigAndCategoryToRecipeId,
} = homepageSlice.actions;

export const RecipeListSelector = ((state: RootState) => state.homepage.recipeList);
export const IngredientstSelector = ((state: RootState) => state.homepage.ingredients);
export const FavoritesRecipeSelector = ((state: RootState) => state.homepage.userFavoriteRecipeIds);
export const HomeConfigVersionSelector = ((state: RootState) => state.homepage.homeVersion);
export const HomeConfigSelector = ((state: RootState) => state.homepage.homeConfig);

export default homepageSlice.reducer;